import React from 'react'
import clsx from 'clsx'
import { styled } from '@mui/material/styles'
import { makeStyles } from 'tss-react/mui'
import { Logger } from 'wdc-cube'
import {
    classToFComponent,
    FCClassContext,
    ListagemTab,
    ListagemTabs,
    ModuleView,
    PageSubtitle,
    PageTitle,
    ScrollablePageView,
    ToolbarDivider,
    ToolbarSectionTitleResponsive
} from 'src/utils/views'

import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import Badge from '@mui/material/Badge'

import type { AppliedFilterSummary } from '../../the-consumer-market/tcm_scopes'

import type { MercadoVetTab } from '../mv_keys'
import { AppliedFiltersScope, HeaderScope, MercadoVetScope } from '../mv_scopes'
import { CnpjPageView } from './mv_cnpj_page'
import { SearchBarView } from './mv_components'
import { MvFilterPaneView } from './mv_filter_panes'
import { LinkedInPageView } from './mv_linkedin_page'
import { WebPageView } from './mv_web_page'

const LOG = Logger.get('MV-View')

const PageHead = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 24,
    '@media (max-width: 720px)': { flexDirection: 'column', gap: 12 }
})

const InfoButton = styled(IconButton)({
    width: 34,
    height: 34,
    flex: '0 0 auto',
    borderRadius: 8,
    color: 'var(--muted)',
    '&:hover': { color: 'var(--green-800)', backgroundColor: 'var(--green-050)' },
    '& .MuiSvgIcon-root': { fontSize: 20 }
})

const TipRow = styled('div')({
    display: 'flex',
    gap: 10,
    justifyContent: 'space-between',
    lineHeight: 1.5,
    whiteSpace: 'nowrap'
})

const TipLabel = styled('span')({ color: 'rgba(255, 255, 255, .68)' })
const TipValue = styled('span')({ fontWeight: 700 })

const infoTooltipSx = {
    backgroundColor: '#171b18',
    border: '1px solid #2d382f',
    borderRadius: '10px',
    padding: '10px 12px',
    fontSize: 12,
    boxShadow: 'var(--shadow-2)'
}

const HeadActions = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flex: '0 0 auto'
})

const FilterButton = styled(IconButton, { shouldForwardProp: (prop) => prop !== 'active' })<{ active?: boolean }>(
    ({ active }) => ({
        width: 34,
        height: 34,
        flex: '0 0 auto',
        borderRadius: 8,
        color: active ? 'var(--green-800)' : 'var(--muted)',
        backgroundColor: active ? 'var(--green-050)' : 'transparent',
        '&:hover': { color: 'var(--green-800)', backgroundColor: 'var(--green-050)' },
        '& .MuiSvgIcon-root': { fontSize: 20 }
    })
)

const filterBadgeSx = {
    '& .MuiBadge-badge': {
        top: 2,
        right: 2,
        height: 14,
        minWidth: 14,
        padding: '0 4px',
        fontSize: 9.5,
        fontWeight: 700,
        backgroundColor: 'var(--green-800)',
        color: '#fff'
    }
}

const TipHead = styled('div')({
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '.4px',
    textTransform: 'uppercase',
    color: 'rgba(255, 255, 255, .5)',
    marginBottom: 8
})

const TipList = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: 7,
    maxHeight: 320,
    overflowY: 'auto'
})

const TipFilterRow = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'minmax(80px, max-content) 1fr',
    gap: 10,
    alignItems: 'baseline'
})

const TagRow = styled('div')({
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 4
})

const TagPrefix = styled('span')({
    fontSize: 11,
    color: 'rgba(255, 255, 255, .5)'
})

const Tag = styled('span')({
    borderRadius: 999,
    border: '1px solid rgba(255, 255, 255, .16)',
    backgroundColor: 'rgba(255, 255, 255, .08)',
    padding: '1px 7px',
    fontSize: 11,
    fontWeight: 600,
    whiteSpace: 'nowrap'
})

const TagMore = styled(Tag)({
    color: '#9fe0a6',
    borderColor: 'rgba(159, 224, 166, .32)',
    backgroundColor: 'rgba(159, 224, 166, .12)'
})

const TabsRow = styled('div')({
    display: 'flex',
    alignItems: 'flex-end',
    borderBottom: '1px solid var(--border)',
    flexShrink: 0
})

const useStyles = makeStyles()({
    overlay: {
        flexShrink: 0,
        marginRight: 5,
        marginBottom: 5
    },
    tabelaMaximizada: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0,
        padding: '26px 28px 42px',
        '@media (max-width: 900px)': { padding: '22px 18px 36px' }
    },
    tabelaMaximizadaConteudo: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minWidth: 0
    }
})

const TABS: { value: MercadoVetTab; label: string }[] = [
    { value: 'cnpj', label: 'Mercado Veterinário' },
    { value: 'web', label: 'Mercado Veterinário WEB' },
    { value: 'linkedin', label: 'Contatos LinkedIn' }
]

const SUBTITLES: Record<MercadoVetTab, string> = {
    cnpj: 'Clínicas e hospitais veterinários com registro de domínio — clique numa atividade ou palavra para restringir o recorte.',
    web: 'Clínicas e hospitais veterinários encontrados no Google Maps.',
    linkedin: 'Contatos no LinkedIn das empresas veterinárias do recorte atual.'
}

type MercadoVetViewProps = {
    className?: string
    scope: MercadoVetScope
}

class MercadoVetViewClass implements FCClassContext<MercadoVetViewProps> {
    scope!: MercadoVetScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState({ scope }: MercadoVetViewProps) {
        this.scope = scope
        this.classes = useStyles().classes
    }

    readonly emitTabChange = (_evt: React.SyntheticEvent, tab: MercadoVetTab) => {
        this.scope.onTabChange(tab).catch(LOG.caught)
    }

    renderAppliedFilters() {
        const { scope } = this
        if (scope.tab === 'linkedin') return null
        if (scope.tab === 'cnpj' && scope.cnpj.detalhar) return null
        // Remonta ao trocar de aba, para observar o scope da aba certa.
        return <AppliedFiltersView key={scope.tab} scope={scope[scope.tab].appliedFilters} />
    }

    renderInfo() {
        const { atualizadoEm, dataDeCorte, indice } = this.scope
        if (!atualizadoEm && !dataDeCorte) return null
        return (
            <Tooltip
                arrow
                placement="left"
                slotProps={{ tooltip: { sx: infoTooltipSx } }}
                title={
                    <>
                        {!!indice && (
                            <TipRow>
                                <TipLabel>Base</TipLabel>
                                <TipValue>{indice}</TipValue>
                            </TipRow>
                        )}
                        {!!atualizadoEm && (
                            <TipRow>
                                <TipLabel>Atualizado em</TipLabel>
                                <TipValue>{atualizadoEm}</TipValue>
                            </TipRow>
                        )}
                        {!!dataDeCorte && (
                            <TipRow>
                                <TipLabel>Data de corte</TipLabel>
                                <TipValue>{dataDeCorte}</TipValue>
                            </TipRow>
                        )}
                    </>
                }
            >
                <InfoButton size="small" aria-label="Datas de referência dos dados">
                    <InfoOutlinedIcon />
                </InfoButton>
            </Tooltip>
        )
    }

    renderTab() {
        const { scope } = this
        switch (scope.tab) {
            case 'web':
                return <WebPageView scope={scope.web} />
            case 'linkedin':
                return <LinkedInPageView scope={scope.linkedin} />
            default:
                return <CnpjPageView scope={scope.cnpj} />
        }
    }

    renderFilterPane() {
        const { classes, scope } = this
        if (scope.tab === 'web') {
            return <MvFilterPaneView className={classes.overlay} scope={scope.web.filtros} />
        }
        if (scope.tab === 'cnpj' && scope.cnpj.detalhar) {
            return null
        }
        return <MvFilterPaneView className={classes.overlay} scope={scope.cnpj.filtros} />
    }

    render({ className }: MercadoVetViewProps) {
        const { classes, scope } = this
        if (scope.tab === 'cnpj' && scope.cnpj.detalhar) {
            return (
                <ModuleView className={className}>
                    <div className={classes.tabelaMaximizada}>
                        <CnpjPageView className={classes.tabelaMaximizadaConteudo} scope={scope.cnpj} />
                    </div>
                </ModuleView>
            )
        }
        return (
            <ModuleView className={className}>
                <ScrollablePageView>
                    <PageHead>
                        <div>
                            <PageTitle>Mercado Veterinário</PageTitle>
                            <PageSubtitle>{SUBTITLES[scope.tab]}</PageSubtitle>
                        </div>
                        <HeadActions>
                            {this.renderAppliedFilters()}
                            {this.renderInfo()}
                        </HeadActions>
                    </PageHead>
                    <TabsRow>
                        <ListagemTabs value={scope.tab} onChange={this.emitTabChange} variant="scrollable">
                            {TABS.map((tab) => (
                                <ListagemTab key={tab.value} value={tab.value} label={tab.label} />
                            ))}
                        </ListagemTabs>
                    </TabsRow>
                    {this.renderTab()}
                </ScrollablePageView>
                {this.renderFilterPane()}
            </ModuleView>
        )
    }
}

// :: Filtros aplicados (botão com contador)

type AppliedFiltersViewProps = {
    scope: AppliedFiltersScope
}

class AppliedFiltersViewClass implements FCClassContext<AppliedFiltersViewProps> {
    scope!: AppliedFiltersScope

    onSyncState({ scope }: AppliedFiltersViewProps) {
        this.scope = scope
    }

    render() {
        const filters = this.scope.filters
        const count = filters.length
        return (
            <Tooltip
                arrow
                placement="left"
                slotProps={{ tooltip: { sx: count > 0 ? { ...infoTooltipSx, maxWidth: 460 } : infoTooltipSx } }}
                title={this.renderAppliedFiltersTip(filters)}
            >
                <FilterButton
                    size="small"
                    active={count > 0}
                    aria-label={count > 0 ? `${count} campos filtrados` : 'Sem filtro aplicado'}
                >
                    <Badge sx={filterBadgeSx} badgeContent={count} overlap="circular">
                        <FilterAltOutlinedIcon />
                    </Badge>
                </FilterButton>
            </Tooltip>
        )
    }

    renderAppliedFiltersTip(filters: AppliedFilterSummary[]) {
        if (filters.length === 0) {
            return (
                <TipRow>
                    <TipLabel>Sem filtro aplicado</TipLabel>
                </TipRow>
            )
        }
        return (
            <>
                <TipHead>{filters.length === 1 ? '1 campo filtrado' : `${filters.length} campos filtrados`}</TipHead>
                <TipList>
                    {filters.map((filter, index) => (
                        <TipFilterRow key={`${filter.caption}-${index}`}>
                            <TipLabel>{filter.caption}</TipLabel>
                            <TagRow>
                                {filter.parts.map((part, partIndex) => (
                                    <React.Fragment key={partIndex}>
                                        {!!part.prefix && <TagPrefix>{part.prefix}</TagPrefix>}
                                        {part.tags.map((tag, tagIndex) => (
                                            <Tag key={`${tag}-${tagIndex}`}>{tag}</Tag>
                                        ))}
                                        {part.hiddenCount > 0 && <TagMore>+{part.hiddenCount}</TagMore>}
                                    </React.Fragment>
                                ))}
                            </TagRow>
                        </TipFilterRow>
                    ))}
                </TipList>
            </>
        )
    }
}

const AppliedFiltersView = classToFComponent(AppliedFiltersViewClass, React)

export const MercadoVetView = classToFComponent(MercadoVetViewClass, React)
export default MercadoVetView

// :: Barra superior

const useHeaderStyles = makeStyles()({
    view: {
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'stretch',
        flexGrow: 1,
        gap: 12,
        marginLeft: 5,
        minWidth: 0,
        width: 0
    },
    searchBar: {
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        width: 0,
        minWidth: 220,
        '@media (max-width: 720px)': { display: 'none' }
    }
})

type HeaderViewProps = {
    className?: string
    scope: HeaderScope
}

class HeaderViewClass implements FCClassContext<HeaderViewProps> {
    render({ className, scope }: HeaderViewProps) {
        const { classes } = useHeaderStyles()
        return (
            <div className={clsx(classes.view, className)}>
                <ToolbarDivider orientation="vertical" variant="middle" flexItem />
                <ToolbarSectionTitleResponsive>Mercado Veterinário</ToolbarSectionTitleResponsive>
                {scope.busca && (
                    <div className={classes.searchBar}>
                        <SearchBarView key={scope.buscaAba} scope={scope.busca} />
                    </div>
                )}
            </div>
        )
    }
}

export const HeaderView = classToFComponent(HeaderViewClass, React)
